import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { GenesysCloudAPIRegion, GenesysCloudAPI } from '@dictionaries/genesys-cloud-aws-region-dictionary';

export interface GCAccessTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

interface PKCETokenResponse {
  data: GCAccessTokenResponse | null;
  status: number;
  success: boolean;
}

const STORAGE_KEYS = {
  verifier: 'gc-pkce-code-verifier',
  challenge: 'gc-pkce-code-challenge',
  shortName: 'gc-pkce-short-name',
  session: 'gc-pkce-session',
} as const;

/**
 * @see https://developer.genesys.cloud/authorization/platform-auth/guides/oauth-auth-code-guide
 */
@Injectable({
  providedIn: 'root',
})
export class PlatformAPIClientAuthorizationService {
  private http = inject(HttpClient);
  private router = inject(Router);

  private accessToken = new BehaviorSubject<string | null>(null);

  constructor() {
    const stored = this.getPKCETokenFromSession();
    if (stored?.access_token) {
      this.accessToken.next(stored.access_token);
    }
  }

  public getAccessToken(): Observable<string | null> {
    return this.accessToken.asObservable();
  }

  private get redirectUri(): string {
    return `${window.location.protocol}//${window.location.host}/authorization/callback`;
  }

  public setSelectedRegion(region: GenesysCloudAPIRegion): void {
    sessionStorage.setItem(STORAGE_KEYS.shortName, region.env);
  }

  public getSelectedRegion(): GenesysCloudAPIRegion | null {
    const env = sessionStorage.getItem(STORAGE_KEYS.shortName);
    if (!env) return null;
    return GenesysCloudAPI.find((r) => r.env === env) ?? null;
  }

  /**
   * Initiates PKCE authorization flow by redirecting to Genesys Cloud login
   */
  public async initializeAuth(): Promise<void> {
    const region = this.getSelectedRegion();
    if (!region) {
      console.error('No region selected');
      return;
    }
    const authUrl = region.auth;

    if (!this.isValidGenesysCloudUrl(authUrl)) {
      console.error('Invalid Genesys Cloud authorization URL');
      return;
    }

    const codeVerifier = this.generateRandomBase64String();
    const codeChallenge = await this.computeCodeChallengeFromVerifier(codeVerifier);

    sessionStorage.setItem(STORAGE_KEYS.verifier, codeVerifier);
    sessionStorage.setItem(STORAGE_KEYS.challenge, codeChallenge);

    const params = new URLSearchParams({
      client_id: environment.genesysCloud.clientId,
      redirect_uri: this.redirectUri,
      response_type: 'code',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      prompt: 'login',
    });

    window.location.href = `${authUrl}/oauth/authorize?${params.toString()}`;
  }

  /**
   * Exchanges the authorization code for an access token
   */
  public exchangeCodeForToken(code: string): Observable<PKCETokenResponse> {
    const region = this.getSelectedRegion();
    const codeVerifier = sessionStorage.getItem(STORAGE_KEYS.verifier);

    const emptyResponse: PKCETokenResponse = { data: null, status: 0, success: false };

    if (!region) {
      console.error('No region found in session');
      return of(emptyResponse);
    }

    if (!codeVerifier) {
      console.error('No PKCE code verifier found in session');
      return of(emptyResponse);
    }

    const authUrl = region.auth;

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: this.redirectUri,
      client_id: environment.genesysCloud.clientId,
      code_verifier: codeVerifier,
      code_challenge_method: 'S256',
    });

    return this.http
      .post<GCAccessTokenResponse>(`${authUrl}/oauth/token`, body.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        observe: 'response',
      })
      .pipe(
        map((response) => ({
          data: response.body,
          status: response.status,
          success: true,
        })),
        catchError((error) => {
          console.error('PKCE token exchange error:', error);
          return of({ data: null, status: error.status, success: false });
        }),
      );
  }

  public getPKCETokenFromSession(): GCAccessTokenResponse | null {
    const stored = sessionStorage.getItem(STORAGE_KEYS.session);
    if (!stored) return null;
    try {
      return JSON.parse(stored) as GCAccessTokenResponse;
    } catch {
      return null;
    }
  }

  public setPKCEToken(token: GCAccessTokenResponse): void {
    sessionStorage.setItem(STORAGE_KEYS.session, JSON.stringify(token));
    this.accessToken.next(token.access_token);
  }

  public clearSession(): void {
    Object.values(STORAGE_KEYS).forEach((key) => sessionStorage.removeItem(key));
    this.accessToken.next(null);
  }

  public getCurrentUser(): Observable<any> {
    const region = this.getSelectedRegion();
    const apiUrl = region?.api ?? GenesysCloudAPI.find((r) => r.env === 'mypurecloud.com')!.api;
    const expandParams = {
      expand: ['geolocation', 'authorization', 'locations', 'languages', 'languagePreference', 'organization'],
    };
    return this.http.get(`${apiUrl}/api/v2/users/me`, { params: expandParams }).pipe(
      catchError((error) => {
        console.error('Error fetching current user:', error);
        throw error;
      }),
    );
  }

  /**
   * Generates a cryptographically secure random string for PKCE code verifier
   */
  public generateRandomBase64String(length = 128): string {
    const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-._~';
    return [...crypto.getRandomValues(new Uint32Array(length))].map((x) => chars[x % chars.length]).join('');
  }

  /**
   * Computes SHA-256 code challenge from verifier
   */
  private async computeCodeChallengeFromVerifier(verifier: string): Promise<string> {
    if (verifier.length < 43 || verifier.length > 128) {
      throw new Error('PKCE Code Verifier length must be between 43 and 128 characters');
    }
    const utf8 = new TextEncoder().encode(verifier);
    const hashBuffer = await crypto.subtle.digest('SHA-256', utf8);
    const hashBase64 = btoa(String.fromCharCode(...new Uint8Array(hashBuffer)));
    return hashBase64.replaceAll('+', '-').replaceAll('/', '_').split('=')[0];
  }

  private isValidGenesysCloudUrl(url: string): boolean {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      const trustedDomains = GenesysCloudAPI.map((r) => new URL(r.auth).hostname);
      return trustedDomains.includes(urlObj.hostname) && urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  }
}
